import React, { useState } from 'react';

// Types
export type QuestionType = 'single' | 'text';

export interface QuizQuestion {
    id: string;
    question: string;
    type: QuestionType;
    options?: string[];
    correctAnswer?: string;
    required?: boolean;
}

export interface UserAnswer {
    questionId: string;
    question: string;
    questionAnswer: string;
    questionCorrectAnswer?: string;
    isCorrect?: boolean;
}

interface QuizProps {
    questions: QuizQuestion[];
    onQuizComplete?: (answers: UserAnswer[]) => void;
}

const answersColors = {
    right: 'green',
    wrong: 'red',
    neutral: '#2c5aa0',
};

const Quiz = ({ questions, onQuizComplete }: QuizProps) => {
    const [ currentIndex, setCurrentIndex ] = useState(0);
    const [ answers, setAnswers ] = useState<UserAnswer[]>([]);
    const [ showResult, setShowResult ] = useState(false);
    const [ filter, setFilter ] = useState<'all' | 'correct' | 'incorrect'>('all');

    const currentQuestion = questions[currentIndex];
    const existingAnswer = answers.find((a) => a.questionId === currentQuestion.id);

    const handleSingleChoice = (option: string) => {
        if (existingAnswer) {
            return;
        }
        const isCorrect =
            currentQuestion.correctAnswer !== undefined
                ? option === currentQuestion.correctAnswer
                : undefined;
        setAnswers((prev) => [ ...prev, {
            questionId: currentQuestion.id,
            question: currentQuestion.question,
            questionAnswer: option,
            questionCorrectAnswer: currentQuestion.correctAnswer,
            isCorrect,
        } ]);
    };

    const handleTextInput = (text: string) => {
        const isCorrect =
            currentQuestion.correctAnswer !== undefined
                ? text.trim().toLowerCase() === currentQuestion.correctAnswer.trim().toLowerCase()
                : undefined;
        setAnswers((prev) => {
            const withoutCurrent = prev.filter((a) => a.questionId !== currentQuestion.id);
            return [
                ...withoutCurrent,
                {
                    questionId: currentQuestion.id,
                    question: currentQuestion.question,
                    questionAnswer: text,
                    questionCorrectAnswer: currentQuestion.correctAnswer,
                    isCorrect,
                },
            ];
        });
    };

    const nextQuestion = () => {
        if (currentIndex + 1 < questions.length) {
            setCurrentIndex((prev) => prev + 1);
        } else {
            setShowResult(true);
            if (onQuizComplete) {
                onQuizComplete(answers);
            }
        }
    };

    if (showResult) {
        const filteredQuestions = questions.filter((q) => {
            const ans = answers.find((a) => a.questionId === q.id);
            if (filter === 'correct') {
                return ans?.isCorrect === true;
            }
            if (filter === 'incorrect') {
                return ans?.isCorrect === false;
            }
            return true;
        });

        const correctCount = answers.filter((a) => a.isCorrect === true).length;
        const correctAnswerCount = questions.filter((q) => q.correctAnswer).length;

        return (
            <div style={{ transition: 'opacity 0.3s ease' }}>
                <h2>Thank you for completing your assessment.</h2>
                {correctAnswerCount > 0 && (
                    <>
                        <p>You answered correctly {correctCount} out of {correctAnswerCount} questions.</p>

                        <label>
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value as any)}
                                style={{
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="all">All</option>
                                <option value="correct">Correct</option>
                                <option value="incorrect">Incorrect</option>
                            </select>
                        </label>
                    </>
                )}

                {filteredQuestions.map((q, i) => {
                    const userAns = answers.find((a) => a.questionId === q.id);

                    return (
                        <div
                            key={q.id}
                            style={{
                                margin: '1rem 0',
                                padding: '1rem',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                            }}
                        >
                            <div>
                                <h3 style={{ marginTop: 4 }}>Question {i + 1}</h3>
                                <p><strong>{q.question}</strong></p>
                            </div>

                            {q.type === 'single' && (
                                <div>
                                    {q.options?.map((opt) => {
                                        const needsValidation = q.correctAnswer;
                                        const isSelected = userAns?.questionAnswer === opt;
                                        const isCorrectAnswer = q.correctAnswer === opt;
                                        const bgColor = isSelected
                                            ? needsValidation
                                                ? isCorrectAnswer
                                                    ? answersColors.right
                                                    : answersColors.wrong
                                                : answersColors.neutral
                                            : '#eee';

                                        return (
                                            <div
                                                key={opt}
                                                style={{
                                                    backgroundColor: bgColor,
                                                    color: bgColor === '#eee' ? 'black' : 'white',
                                                    padding: '10px',
                                                    marginBottom: '6px',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                {opt}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {q.type === 'text' && (
                                <p>
                                    Your answer: <em>{userAns?.questionAnswer || '—'}</em>
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    const isAnswerMissing = currentQuestion.required && (
        !existingAnswer || !existingAnswer.questionAnswer?.toString().trim()
    );

    return (
        <div style={{ transition: 'opacity 0.3s ease' }}>
            <h3>Question {currentIndex + 1} / {questions.length}</h3>
            <div style={{ height: '8px', background: '#eee', borderRadius: '4px', marginBottom: '1rem' }}>
                <div
                    style={{
                        width: `${(
                            (
                                currentIndex + 1
                            ) / questions.length
                        ) * 100}%`,
                        backgroundColor: '#2c5aa0',
                        height: '100%',
                        borderRadius: '4px',
                        transition: 'width 0.3s ease',
                    }}
                ></div>
            </div>

            <p>{currentQuestion.question}</p>

            {currentQuestion.type === 'single' && (
                <div>
                    {currentQuestion.options?.map((option) => {
                        const answered = !!existingAnswer;
                        const isSelected = existingAnswer?.questionAnswer === option;
                        const isCorrect = currentQuestion.correctAnswer === option;
                        const needsValidation = currentQuestion.correctAnswer;
                        let optionBackgroundColor = '#eee';
                        if (answered) {
                            if (isSelected) {
                                if (needsValidation) {
                                    if (isCorrect) {
                                        optionBackgroundColor = answersColors.right;
                                    } else {
                                        optionBackgroundColor = answersColors.wrong;
                                    }

                                } else {
                                    optionBackgroundColor = answersColors.neutral;
                                }
                            }
                        }

                        return (
                            <button
                                key={option}
                                onClick={() => handleSingleChoice(option)}
                                disabled={answered}
                                style={{
                                    marginBottom: '0.5rem',
                                    backgroundColor: optionBackgroundColor,
                                    color: answered && isSelected ? 'white' : 'black',
                                    padding: '10px 16px',
                                    width: '100%',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: answered ? 'default' : 'pointer',
                                    fontWeight: 'bold',
                                }}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>
            )}

            {currentQuestion.type === 'text' && (
                <input
                    type="text"
                    placeholder="Type your thoughts..."
                    value={existingAnswer?.questionAnswer || ''}
                    onChange={(e) => handleTextInput(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '10px 16px',
                        marginBottom: '1rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                    }}
                />
            )}

            <button
                onClick={nextQuestion}
                disabled={isAnswerMissing}
                style={{
                    backgroundColor: '#2c5aa0',
                    color: '#fff',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: isAnswerMissing ? 'not-allowed' : 'pointer',
                    opacity: isAnswerMissing ? 0.6 : 1,
                }}
            >
                {currentIndex + 1 < questions.length ? 'Next' : 'Finish'}
            </button>
        </div>
    );
};

export default Quiz;
